import { Injectable, inject } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { switchMap, catchError, of, map, mergeMap, debounceTime } from 'rxjs';

import { ChatService } from '@offconon/core-api';

import { ChatActions } from './chat.actions';

@Injectable()
export class ChatEffects {
  private actions$ = inject(Actions);
  private chatService = inject(ChatService);
  private store = inject(Store);
  private translateService = inject(TranslateService);
  private messageService = inject(MessageService);

  fetchMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.fetchMessages),
      debounceTime(1000),
      switchMap((action) => {
        return this.chatService.chatMessagesList({ room_id: action.roomId }).pipe(
          switchMap((messages) => {
            return of(ChatActions.fetchMessagesSuccess({ messages: messages }));
          }),
          catchError((error) => {
            return of(ChatActions.loadChatFailure({ error }));
          }),
        );
      }),
    ),
  );

  fetchRooms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.fetchRooms),
      mergeMap((action) => {
        return this.chatService
          .chatRoomsList(action.page, action.pageSize, {
            id: action.searchJson?.id,
          })
          .pipe(
            mergeMap((paginatedRoomList) => {
              const rooms = paginatedRoomList.results || [];
              return [
                ChatActions.fetchRoomsSuccess({
                  roomType: 'direct',
                  rooms,
                  pagination: {
                    page: paginatedRoomList.current_page_number!,
                    pageSize: paginatedRoomList.page_size!,
                    total: paginatedRoomList.total_objects!,
                    totalPages: paginatedRoomList.total_pages!,
                  },
                }),
                ...rooms.map((room) => ChatActions.fetchRoomMembers({ room_id: room.id })),
              ];
            }),
            catchError((error) => of(ChatActions.loadChatFailure({ error }))),
          );
      }),
    ),
  );

  // Keep fetchRoomMembers$ as is for individual member fetching
  fetchRoomMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.fetchRoomMembers),
      mergeMap(({ room_id }) => {
        return this.chatService.chatRoomMembersList(1, 200, { room_id }).pipe(
          map((paginatedRoomMemberList) => {
            return ChatActions.fetchRoomMembersSuccess({
              paginatedRoomMemberList,
              roomId: room_id!,
            });
          }),
          catchError((error) => {
            return of(ChatActions.loadChatFailure({ error }));
          }),
        );
      }),
    ),
  );

  createRoomMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.createRoomMember),
      switchMap(({ postRoomMemberRequest }) => {
        return this.chatService.chatRoomMembersCreate(postRoomMemberRequest).pipe(
          switchMap((roomMember) => {
            return of(ChatActions.createRoomMemberSuccess({ roomMember }));
          }),
          catchError((error) => {
            return of(ChatActions.loadChatFailure({ error }));
          }),
        );
      }),
    ),
  );

  createRoom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.createRoom),
      switchMap(({ postRoomRequest }) => {
        return this.chatService.chatRoomsCreate(postRoomRequest).pipe(
          switchMap((room) => {
            return of(ChatActions.createRoomSuccess({ room }));
          }),
          catchError((error) => {
            return of(ChatActions.loadChatFailure({ error }));
          }),
        );
      }),
    ),
  );

  selectRoom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.selectRoom),
      mergeMap((action) =>
        of(ChatActions.selectRoomSuccess({ id: action.id })).pipe(
          mergeMap(() =>
            this.chatService.chatMessagesList({ room_id: action.id }).pipe(
              map((messages) => ChatActions.fetchMessagesSuccess({ messages })),
              catchError((error) => of(ChatActions.loadChatFailure({ error }))),
            ),
          ),
        ),
      ),
    ),
  );

  inviteToRoom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.inviteUsersToRoom),
      switchMap((action) => {
        return this.chatService
          .chatRoomsPartialUpdate({
            id: action.roomId,
            to_user_ids: Array.isArray(action.userIds) ? action.userIds : [action.userIds],
          })
          .pipe(
            switchMap(() => {
              return of();
            }),
            catchError((error) => {
              return of(ChatActions.loadChatFailure({ error }));
            }),
          );
      }),
    ),
  );

  deleteRoom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.deleteRoom),

      switchMap(({ id }) => {
        return this.chatService.chatRoomsDestroy(id).pipe(
          switchMap(() => {
            this.messageService.add({
              severity: 'info',
              summary: this.translateService.instant('Delete'),
              detail: this.translateService.instant('The item has been deleted!'),
            });
            return of(ChatActions.deleteRoomSuccess({ id }));
          }),
          catchError((error) => {
            return of(ChatActions.loadChatFailure({ error }));
          }),
        );
      }),
    ),
  );

  leaveRoom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.leaveRoom),
      switchMap(({ roomId }) => {
        return this.chatService.chatRoomMembersDestroy(roomId).pipe(
          switchMap(() => {
            this.messageService.add({
              severity: 'info',
              summary: this.translateService.instant('Leave chat'),
              detail: this.translateService.instant('You have been removed from this chat!'),
            });
            return of(ChatActions.leaveRoomSuccess({ roomId }));
          }),
          catchError((error) => {
            return of(ChatActions.loadChatFailure({ error }));
          }),
        );
      }),
    ),
  );

  updateRoomMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.updateRoomMember),
      switchMap(({ roomMember }) => {
        return this.chatService.chatRoomMembersCreate(roomMember).pipe(
          switchMap((roomMember) => {
            return of(ChatActions.updateRoomMemberSuccess({ roomMember }));
          }),
          catchError((error) => {
            return of(ChatActions.loadChatFailure({ error }));
          }),
        );
      }),
    ),
  );
}
